import * as Sentry from "@sentry/react";
import { ComponentType, lazy } from "react";

// A lazily-imported route chunk can 404 after a redeploy rotates the hashed
// filenames while an old tab is still open ("Failed to fetch dynamically
// imported module"). Recovery happens at the lazy() boundary (see
// lazyWithReload): on a failed import, reload once to fetch the new bundle and
// return a promise that never resolves so React stays in Suspense (Preloader)
// until navigation - rather than resolving undefined (which makes React.lazy
// throw "reading 'default'") or flashing the ErrorBoundary. A second failure
// within RELOAD_WINDOW_MS means the deployed bundle is genuinely broken: log it,
// report to Sentry, and rethrow so the ErrorBoundary shows.

export const RELOAD_KEY = "vitePreloadReloadAt";
export const RELOAD_WINDOW_MS = 10000;

export const recoverFromChunkError = <T>(
	error: unknown,
	name: string,
): Promise<T> => {
	const detail = error instanceof Error ? error.message : String(error);

	let reloadedRecently = false;
	try {
		const now = Date.now();
		const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? "0");
		reloadedRecently = now - last < RELOAD_WINDOW_MS;
		if (!reloadedRecently) sessionStorage.setItem(RELOAD_KEY, String(now));
	} catch {
		// sessionStorage unavailable (e.g. private mode) - treat as a first
		// attempt and reload once.
	}

	if (reloadedRecently) {
		console.error(
			`[lazyWithReload] "${name}" chunk still failing after a recovery reload - not reloading again. The deployed bundle may be missing this chunk. ${detail}`,
			error,
		);
		Sentry.captureException(
			error instanceof Error
				? error
				: new Error(`lazyWithReload ${name}: ${detail}`),
			{ tags: { preloadError: "persistent", chunk: name } },
		);
		// The one recovery reload didn't fix it, so the deployed bundle is
		// genuinely missing this chunk. Show the maintenance-aware boot-error page
		// (index.html) rather than rethrowing to the raw ErrorBoundary - the router
		// (and its /maintenance route) lives in the chunk that just failed, so it
		// can't render. Stay suspended while that page replaces the document.
		if (typeof window !== "undefined" && window.__showBootError) {
			window.__showBootError();
			return new Promise<T>(() => {});
		}
		throw error;
	}

	console.warn(
		`[lazyWithReload] "${name}" chunk failed to load - most likely a stale tab after a redeploy. Reloading once to pick up the new bundle. ${detail}`,
	);
	window.location.reload();
	// Stay suspended (Preloader visible) until the reload navigates.
	return new Promise<T>(() => {});
};

export const lazyWithReload = <T extends ComponentType<any>>(
	factory: () => Promise<{ default: T }>,
	name: string,
) => lazy(() => factory().catch((error) => recoverFromChunkError<{ default: T }>(error, name)));
